import { browserAccountStateSessionRepository } from "./account-state-session-repository.js";
import { desktopPrivacyApiUrl, desktopPrivacyPagePath } from "./desktop-privacy-client.js";

// Footer discovery and participation controls project the same endpoint decision.
// No document body is cached, and ordinary Terms-gated session discovery cannot
// decide whether an authenticated person may read this notice.
function mountNoticeAccess() {
  const surfaces = [...document.querySelectorAll("[data-desktop-privacy-access]")];
  if (surfaces.length === 0) return () => {};
  let request = null;
  let disposed = false;

  function localizeLinks() {
    if (disposed) return;
    const path = desktopPrivacyPagePath();
    for (const surface of surfaces) {
      for (const link of surface.querySelectorAll("[data-desktop-privacy-links] [data-desktop-privacy-localized]")) {
        link.href = path;
      }
      window.__fishystuffLanguage?.apply(surface);
    }
  }

  function present(allowed) {
    for (const surface of surfaces) {
      const links = surface.querySelector("[data-desktop-privacy-links]");
      const template = surface.querySelector("[data-desktop-privacy-links-template]");
      const instructions = surface.querySelector("[data-desktop-privacy-request]");
      if (!links || !template) continue;
      links.replaceChildren(...(allowed ? [template.content.cloneNode(true)] : []));
      links.hidden = !allowed;
      if (instructions) instructions.hidden = allowed;
    }
    localizeLinks();
  }

  async function checkAccess() {
    const current = new AbortController();
    request = current;
    try {
      const response = await fetch(desktopPrivacyApiUrl(), {
        method: "GET", cache: "no-store", credentials: "include", redirect: "error",
        headers: { Accept: "text/html" }, signal: current.signal,
      });
      await response.body?.cancel();
      if (current.signal.aborted) return;
      if (response.status === 401 || response.status === 403) return;
      if (response.status !== 200
          || response.headers.get("content-type")?.split(";")[0] !== "text/html") {
        throw new TypeError("Desktop privacy notice access could not be checked.");
      }
      present(true);
    } catch (error) {
      if (!current.signal.aborted) globalThis.reportError?.(error);
    } finally {
      if (request === current) request = null;
    }
  }

  const repository = browserAccountStateSessionRepository();
  const unsubscribe = repository.subscribe((state) => {
    request?.abort();
    request = null;
    present(false);
    if (state.kind === "ready" || state.kind === "unavailable") void checkAccess();
  });
  void repository.load();
  const language = window.__fishystuffLanguage;
  language?.ready.then(localizeLinks);
  if (language) window.addEventListener(language.event, localizeLinks);
  return () => {
    disposed = true;
    request?.abort();
    if (language) window.removeEventListener(language.event, localizeLinks);
    unsubscribe();
    present(false);
  };
}

let dispose = mountNoticeAccess();
window.addEventListener("pagehide", () => { dispose(); });
window.addEventListener("pageshow", (event) => {
  if (event.persisted) dispose = mountNoticeAccess();
});
